import { useCallback, useState } from "react";
import { useActivateQuestionnaire } from "../custom-hooks/queries";
import {
  canReopenCompleteVqq,
  canReopenVqqRequest,
} from "../utils/rfiRequestActionPermissions";

/**
 * Shared Complete → reopen flow for My VQQs menu and VQQ Details.
 * Keeps callers thin for Sonar new-code coverage.
 */
export function useReopenCompleteVqq({
  status,
  details,
  userRole,
  questionnaireId,
  enabled = true,
  onSuccess,
  onError,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { mutate, isPending, isError, error } = useActivateQuestionnaire();

  const canReopen =
    Boolean(enabled) &&
    questionnaireId != null &&
    Number(questionnaireId) > 0 &&
    canReopenVqqRequest(userRole) &&
    canReopenCompleteVqq(status, details);

  const openConfirm = useCallback(() => {
    if (!canReopen) return;
    setConfirmOpen(true);
  }, [canReopen]);

  const closeConfirm = useCallback(() => {
    if (!isPending) setConfirmOpen(false);
  }, [isPending]);

  const confirmReopen = useCallback(() => {
    if (!canReopen) return;
    mutate(
      { id: Number(questionnaireId) },
      {
        onSuccess: (data) => {
          setConfirmOpen(false);
          onSuccess?.(data);
        },
        onError: (err) => {
          onError?.(err);
        },
      }
    );
  }, [canReopen, mutate, questionnaireId, onSuccess, onError]);

  return {
    canReopen,
    confirmOpen,
    openConfirm,
    closeConfirm,
    confirmReopen,
    isPending,
    isError,
    error,
  };
}
